import {Autocomplete, TextField} from "@mui/material";
import {useQuery} from "@tanstack/react-query";
import {useLocalization} from "@smalox/shared";
import React, {useEffect, useState} from "react";
import {Tenant, useTenantApi} from "../api";
import {queryKeys} from "../utils/queryKeys";

interface TenantSelectProps {
	value: Tenant | null;
	onChange: (tenant: Tenant | null) => void;
	label?: string;
	fullWidth?: boolean;
}

export function TenantSelect({value, onChange, label, fullWidth}: TenantSelectProps) {
	const {t} = useLocalization();
	const {queryTenants} = useTenantApi();

	const [input, setInput] = useState("");
	const [search, setSearch] = useState("");

	// Debounce the typed input before querying.
	useEffect(() => {
		const handle = setTimeout(() => setSearch(input), 300);
		return () => clearTimeout(handle);
	}, [input]);

	const {data, isLoading} = useQuery(
		queryKeys.tenants({search, size: 20}),
		() => queryTenants({search: search || undefined, size: 20}),
	);

	const options = data?.content ?? [];

	return (
		<Autocomplete<Tenant>
			value={value}
			onChange={(_, tenant) => onChange(tenant)}
			onInputChange={(_, newInput) => setInput(newInput)}
			options={options}
			loading={isLoading}
			getOptionLabel={tenant => tenant.name ?? tenant.id}
			isOptionEqualToValue={(option, selected) => option.id === selected.id}
			fullWidth={fullWidth}
			renderInput={params => <TextField {...params} label={label ?? t("tenants.single")} />}
		/>
	);
}
